import React, { useContext } from "react";
import { FormattedMessage } from "react-intl-phraseapp";
import { Button, Card } from "@cmsgov/ds-medicare-gov";
import {
  useTranslate,
  $$Whole,
  sendTealiumEvent,
  $$,
  useFormatMessage,
} from "../../../helpers";
import { CheckmarkIcon, Tooltip } from "../../../components";
import {
  planBenefitMapping,
  MedigapPlan,
  MedigapPlanPremiumRange,
  MedigapPlanType,
} from "../utils/plans";
import PhasedOutDisclaimer from "./PhasedOutDisclaimer";
import { MedigapBenefitsHelpDrawer } from "../HelpDrawers/MedigapBenefitsHelpDrawer";
import planDataUtils from "../../../helpers/planDataUtils";
import { useFlags } from "launchdarkly-react-client-sdk";
import routes from "../../../app/routes";
import { Link } from "react-router-dom";
import { AppContext } from "../../../app/store";
import { Gender } from "../../../@types";

interface MedigapPlanCardProps {
  plan: MedigapPlan;
  premiumRange: MedigapPlanPremiumRange;
}

const MedigapPlanCard: React.FunctionComponent<MedigapPlanCardProps> = ({
  plan,
  premiumRange,
}) => {
  const t = useTranslate();
  const {
    state: { demographicInfo },
  } = useContext(AppContext);
  const { mctCurrentPlanYear } = useFlags();
  const FM = useFormatMessage();
  const planBenefits = plan.plan_benefits.split(",");
  const partBPremium = planDataUtils.getPartBPremiumForYear(
    mctCurrentPlanYear || "2020"
  );
  const hasCopayTooltip = [MedigapPlanType.N, MedigapPlanType.MN_N].includes(
    plan.plan_type as MedigapPlanType
  );
  let monthlyCostMessage = [t("medigap.premiums_range.demo_data.none")];

  if (demographicInfo) {
    const { age, gender, tobacco } = demographicInfo;
    monthlyCostMessage = [t("medigap.premiums_range.demo_data.for_a")];
    if (age) {
      monthlyCostMessage.push(
        Number(age) > 64
          ? FM(
              { id: "medigap.premiums_range.demo_data.year_old" },
              { age: age }
            )
          : t("medigap.premiums_range.demo_data.younger_than_65")
      );
    }
    if (gender === Gender.FEMALE) {
      monthlyCostMessage.push(t("enroll_form.female"));
    } else if (gender === Gender.MALE) {
      monthlyCostMessage.push(t("enroll_form.male"));
    }
    if (tobacco !== undefined) {
      if (!age && !gender) {
        monthlyCostMessage.push(t("medigap.premiums_range.demo_data.person"));
      }
      monthlyCostMessage.push(
        tobacco
          ? t("medigap.premiums_range.demo_data.tobacco_yes")
          : t("medigap.premiums_range.demo_data.tobacco_no")
      );
    }
  }

  const focusOnDemographicComponent = (): void => {
    sendTealiumEvent(
      "Step 1: Decide what plans you want",
      `Result: Get a more accurate price Medigap Plan ${plan.plan_type}`
    );
    const focusElement = document.querySelector(
      ".AccuratePriceContainer"
    ) as HTMLInputElement;
    focusElement.focus();
  };

  return (
    <Card className="MedigapPlanCard">
      <section className="MedigapPlanCard__header">
        <h2>{t(plan.plan_name)}</h2>
        <PhasedOutDisclaimer plan={plan} className="MedigapPlanCard__info" />
      </section>
      <section className="MedigapPlanCard__actions">
        <div className="mct-hide-print">
          <Link
            to={routes.medigap.planDetails.replace(
              ":medigapPlanType",
              plan.plan_type
            )}
            className="ds-c-button ds-c-button--primary-alt"
            onClick={(): void => {
              sendTealiumEvent(
                "Step 1: Decide what plans you want",
                `CTA: Plan Details Medigap Plan ${plan.plan_type}`
              );
            }}
          >
            {t("medigap.card.button.plan_details")}
          </Link>
          <Link
            to={routes.medigap.policies.replace(
              ":medigapPlanType",
              plan.plan_type
            )}
            className="ds-c-button ds-c-button--primary"
            onClick={(): void => {
              sendTealiumEvent(
                "Step 1: Decide what plans you want",
                `CTA: View Policies Medigap Plan ${plan.plan_type}`
              );
            }}
          >
            {t("medigap.card.button.view_policies")}
          </Link>
        </div>
      </section>
      <div className="MedigapPlanCard__body">
        <section className="MedigapPlanCard__data_section">
          <h3 className="MedigapPlanCard__section_heading">
            {t("medigap.card.section_heading.monthly_cost")}
          </h3>
          <>
            <FormattedMessage
              id="medigap.premiums_range"
              values={{
                range: (
                  <b>
                    {$$Whole(Math.round(premiumRange.monthly_rate_min))}-
                    {$$Whole(Math.round(premiumRange.monthly_rate_max))}
                  </b>
                ),
              }}
            />{" "}
            {monthlyCostMessage.join(" ")}
          </>
          <p>
            {t("plan_card.does_not_include")}:
            <br />
            {$$(partBPremium)} {t("plan_terms.partb_premium")}
          </p>
          <div className="ds-u-margin-top--2 mct-hide-print">
            <Button
              variation="transparent"
              className="ds-c-button--primary-alt ds-u-padding--0"
              onClick={focusOnDemographicComponent}
            >
              {t("medigap.get_more_accurate_price")}
            </Button>
          </div>
        </section>
        <div className="MedigapPlanCard__divider"></div>
        <div className="MedigapPlanCard__data_section MedigapPlanCard__middle_section">
          <section>
            <h3 className="MedigapPlanCard__section_heading">
              {t("medigap.card.section_heading.copays")}
            </h3>
            <div className="MedigapPlanCard__line_item">
              <b>{t(plan.part_b_coinsurance)}</b>{" "}
              {plan.part_b_coinsurance_note && t(plan.part_b_coinsurance_note)}{" "}
              {hasCopayTooltip && (
                <Tooltip
                  title={t("medigap.20_50_copays")}
                  triggerText={t("medigap.20_50_copays")}
                  inline
                >
                  <p className="ds-u-font-size--small">
                    {t("medigap.20_50_copays.tooltip")}
                  </p>
                </Tooltip>
              )}
            </div>
          </section>
          <section>
            <h3 className="MedigapPlanCard__section_heading">
              {t("medigap.card.section_heading.deductibles")}
            </h3>
            {plan.total_deductible && (
              <div className="MedigapPlanCard__line_item">
                <b>{plan.total_deductible}</b>{" "}
                {t("medigap.total_deductible_info")}
              </div>
            )}
            <div>
              <div className="MedigapPlanCard__line_item">
                <b>{plan.part_a_deductible}</b>{" "}
                {plan.part_a_deductible_note && t(plan.part_a_deductible_note)}{" "}
                <Tooltip
                  title={t("medigap.part_a.deductible")}
                  triggerText={t("medigap.part_a.deductible")}
                  inline
                >
                  <p className="ds-u-font-size--small">
                    {t("medigap.part_a.deductible.tooltip")}
                  </p>
                </Tooltip>
              </div>
              <div className="MedigapPlanCard__line_item">
                <b>{plan.part_b_deductible}</b>{" "}
                {plan.part_b_deductible_note && t(plan.part_b_deductible_note)}{" "}
                <Tooltip
                  title={t("medigap.part_b.deductible")}
                  triggerText={t("medigap.part_b.deductible")}
                  inline
                >
                  <p className="ds-u-font-size--small">
                    {t("medigap.part_b.deductible.tooltip")}
                  </p>
                </Tooltip>
              </div>
            </div>
          </section>
        </div>
        <div className="MedigapPlanCard__divider"></div>
        <section className="MedigapPlanCard__data_section">
          <h3 className="MedigapPlanCard__section_heading">
            {t("medigap.card.section_heading.benefits")}
          </h3>
          <ul className="ds-c-list--bare">
            {Object.keys(planBenefitMapping).map(benefit => {
              const benefitIncluded = planBenefits.includes(benefit);
              return (
                <li
                  key={`${plan.plan_type}_benefit_${benefit}`}
                  className="MedigapPlanCard__benefit"
                >
                  <CheckmarkIcon checked={benefitIncluded} />{" "}
                  {t(planBenefitMapping[benefit])}
                  <span className="sr-only">
                    {benefitIncluded
                      ? ` ${t("plan_card.benefit.is_available")}`
                      : ` ${t("plan_card.benefit.is_not_available")}`}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="ds-u-margin-y--2 mct-hide-print">
            <MedigapBenefitsHelpDrawer
              triggerLabel={t("medigap.compare_to_other_plans")}
              onOpenTealiumProps={{
                event_action: "Step 1: Decide what plans you want",
                event_label: `Result: Compare to other plans ${plan.plan_type}`,
              }}
            />
          </div>
        </section>
      </div>
    </Card>
  );
};

export default MedigapPlanCard;
